import { useState, useRef } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Textarea, Select } from '@/components/ui'
import { FileUser, Download, Plus, Trash2, GripVertical } from 'lucide-react'
import { toast } from 'sonner'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

interface Experience {
  id: string
  title: string
  company: string
  location: string
  startDate: string
  endDate: string
  description: string
}

interface Education {
  id: string
  degree: string
  school: string
  location: string
  year: string
}

interface ResumeData {
  fullName: string
  email: string
  phone: string
  location: string
  summary: string
  experience: Experience[]
  education: Education[]
  skills: string
}

export function ResumeBuilder() {
  const resumeRef = useRef<HTMLDivElement>(null)
  const [template, setTemplate] = useState<'modern' | 'classic' | 'minimal'>('modern')
  const [data, setData] = useState<ResumeData>({
    fullName: 'John Doe',
    email: 'john.doe@email.com',
    phone: '+63 912 345 6789',
    location: 'Manila, Philippines',
    summary: 'Experienced software developer with 5+ years of expertise in building scalable web applications. Passionate about clean code and user-centric design.',
    experience: [
      {
        id: '1',
        title: 'Senior Software Developer',
        company: 'Tech Solutions Inc.',
        location: 'Makati City',
        startDate: '2021',
        endDate: 'Present',
        description: 'Lead development of enterprise web applications using React and Node.js. Mentored junior developers and implemented CI/CD pipelines.',
      },
      {
        id: '2',
        title: 'Software Developer',
        company: 'Digital Agency Co.',
        location: 'Quezon City',
        startDate: '2019',
        endDate: '2021',
        description: 'Developed responsive web applications and RESTful APIs. Collaborated with design team to implement UI/UX improvements.',
      },
    ],
    education: [
      {
        id: '1',
        degree: 'BS Computer Science',
        school: 'University of the Philippines',
        location: 'Diliman, Quezon City',
        year: '2019',
      },
    ],
    skills: 'JavaScript, TypeScript, React, Node.js, Python, SQL, Git, AWS, Docker',
  })

  const updateField = (field: keyof ResumeData, value: string | Experience[] | Education[]) => {
    setData((prev) => ({ ...prev, [field]: value }))
  }

  const addExperience = () => {
    const newExp: Experience = {
      id: crypto.randomUUID(),
      title: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      description: '',
    }
    setData((prev) => ({ ...prev, experience: [...prev.experience, newExp] }))
  }

  const updateExperience = (id: string, field: keyof Experience, value: string) => {
    setData((prev) => ({
      ...prev,
      experience: prev.experience.map((exp) =>
        exp.id === id ? { ...exp, [field]: value } : exp
      ),
    }))
  }

  const removeExperience = (id: string) => {
    setData((prev) => ({
      ...prev,
      experience: prev.experience.filter((exp) => exp.id !== id),
    }))
  }

  const addEducation = () => {
    const newEdu: Education = {
      id: crypto.randomUUID(),
      degree: '',
      school: '',
      location: '',
      year: '',
    }
    setData((prev) => ({ ...prev, education: [...prev.education, newEdu] }))
  }

  const updateEducation = (id: string, field: keyof Education, value: string) => {
    setData((prev) => ({
      ...prev,
      education: prev.education.map((edu) =>
        edu.id === id ? { ...edu, [field]: value } : edu
      ),
    }))
  }

  const removeEducation = (id: string) => {
    setData((prev) => ({
      ...prev,
      education: prev.education.filter((edu) => edu.id !== id),
    }))
  }

  const downloadPDF = async () => {
    if (!resumeRef.current) return

    try {
      const canvas = await html2canvas(resumeRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
      })

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      })

      const imgWidth = 210
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, imgHeight)
      pdf.save(`${data.fullName.replace(/\s+/g, '_')}_Resume.pdf`)
      toast.success('Resume downloaded!')
    } catch {
      toast.error('Failed to generate PDF')
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <FileUser className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Resume Builder</h1>
            <p className="text-muted-foreground">Create a professional resume and download as PDF</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_600px]">
        {/* Editor */}
        <div className="space-y-4">
          {/* Template Selection */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Template</CardTitle>
                <Select value={template} onChange={(e) => setTemplate(e.target.value as 'modern' | 'classic' | 'minimal')}>
                  <option value="modern">Modern</option>
                  <option value="classic">Classic</option>
                  <option value="minimal">Minimal</option>
                </Select>
              </div>
            </CardHeader>
          </Card>

          {/* Personal Info */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Full Name</Label>
                  <Input
                    value={data.fullName}
                    onChange={(e) => updateField('fullName', e.target.value)}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={data.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={data.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="+63 XXX XXX XXXX"
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Location</Label>
                  <Input
                    value={data.location}
                    onChange={(e) => updateField('location', e.target.value)}
                    placeholder="City, Country"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Professional Summary</Label>
                <Textarea
                  value={data.summary}
                  onChange={(e) => updateField('summary', e.target.value)}
                  placeholder="Brief summary of your professional background..."
                  className="min-h-[80px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Experience */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Work Experience</CardTitle>
                <Button variant="outline" size="sm" onClick={addExperience}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {data.experience.map((exp, index) => (
                <div key={exp.id} className="p-4 border border-border rounded-lg bg-muted/20">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <GripVertical className="h-4 w-4" />
                      <span className="text-xs font-medium">Experience {index + 1}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive"
                      onClick={() => removeExperience(exp.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Job Title</Label>
                      <Input
                        value={exp.title}
                        onChange={(e) => updateExperience(exp.id, 'title', e.target.value)}
                        placeholder="Software Developer"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Company</Label>
                      <Input
                        value={exp.company}
                        onChange={(e) => updateExperience(exp.id, 'company', e.target.value)}
                        placeholder="Company Name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Location</Label>
                      <Input
                        value={exp.location}
                        onChange={(e) => updateExperience(exp.id, 'location', e.target.value)}
                        placeholder="City"
                      />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Start</Label>
                        <Input
                          value={exp.startDate}
                          onChange={(e) => updateExperience(exp.id, 'startDate', e.target.value)}
                          placeholder="2020"
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">End</Label>
                        <Input
                          value={exp.endDate}
                          onChange={(e) => updateExperience(exp.id, 'endDate', e.target.value)}
                          placeholder="Present"
                        />
                      </div>
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Textarea
                        value={exp.description}
                        onChange={(e) => updateExperience(exp.id, 'description', e.target.value)}
                        placeholder="Key responsibilities and achievements..."
                        className="min-h-[60px]"
                      />
                    </div>
                  </div>
                </div>
              ))}
              {data.experience.length === 0 && (
                <p className="text-center text-muted-foreground py-4">No experience added yet</p>
              )}
            </CardContent>
          </Card>

          {/* Education */}
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Education</CardTitle>
                <Button variant="outline" size="sm" onClick={addEducation}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {data.education.map((edu, index) => (
                <div key={edu.id} className="p-4 border border-border rounded-lg bg-muted/20">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <GripVertical className="h-4 w-4" />
                      <span className="text-xs font-medium">Education {index + 1}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive"
                      onClick={() => removeEducation(edu.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Degree</Label>
                      <Input
                        value={edu.degree}
                        onChange={(e) => updateEducation(edu.id, 'degree', e.target.value)}
                        placeholder="BS Computer Science"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">School</Label>
                      <Input
                        value={edu.school}
                        onChange={(e) => updateEducation(edu.id, 'school', e.target.value)}
                        placeholder="University Name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Location</Label>
                      <Input
                        value={edu.location}
                        onChange={(e) => updateEducation(edu.id, 'location', e.target.value)}
                        placeholder="City"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Year</Label>
                      <Input
                        value={edu.year}
                        onChange={(e) => updateEducation(edu.id, 'year', e.target.value)}
                        placeholder="2020"
                      />
                    </div>
                  </div>
                </div>
              ))}
              {data.education.length === 0 && (
                <p className="text-center text-muted-foreground py-4">No education added yet</p>
              )}
            </CardContent>
          </Card>

          {/* Skills */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Skills</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={data.skills}
                onChange={(e) => updateField('skills', e.target.value)}
                placeholder="JavaScript, React, Node.js, etc."
                className="min-h-[60px]"
              />
              <p className="text-xs text-muted-foreground mt-2">Separate skills with commas</p>
            </CardContent>
          </Card>

          <Button onClick={downloadPDF} className="w-full">
            <Download className="h-4 w-4 mr-2" />
            Download Resume PDF
          </Button>
        </div>

        {/* Preview */}
        <Card className="glass h-fit sticky top-4">
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>Live preview of your resume</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/20 p-4 rounded-lg overflow-auto max-h-[800px]">
              <div
                ref={resumeRef}
                className={`bg-white text-black shadow-lg ${
                  template === 'modern' ? 'font-sans' : template === 'classic' ? 'font-serif' : 'font-sans'
                }`}
                style={{ width: '595px', minHeight: '842px', padding: '40px' }}
              >
                {/* Header */}
                <div className={`mb-6 ${
                  template === 'modern' ? 'border-b-4 border-teal-500 pb-4' :
                  template === 'classic' ? 'border-b-2 border-gray-800 pb-4 text-center' :
                  'pb-4'
                }`}>
                  <h1 className={`font-bold ${
                    template === 'modern' ? 'text-3xl text-gray-800' :
                    template === 'classic' ? 'text-3xl uppercase tracking-wider' :
                    'text-2xl'
                  }`}>
                    {data.fullName}
                  </h1>
                  <div className={`text-sm mt-2 ${
                    template === 'modern' ? 'text-teal-600 flex gap-4' :
                    template === 'classic' ? 'text-gray-600' :
                    'text-gray-600 flex gap-4'
                  }`}>
                    <span>{data.email}</span>
                    {template !== 'classic' && <span>|</span>}
                    <span>{data.phone}</span>
                    {template !== 'classic' && <span>|</span>}
                    <span>{data.location}</span>
                  </div>
                </div>

                {/* Summary */}
                {data.summary && (
                  <div className="mb-6">
                    <h2 className={`font-bold mb-2 ${
                      template === 'modern' ? 'text-lg text-teal-600 uppercase tracking-wide' :
                      template === 'classic' ? 'text-lg border-b border-gray-400 pb-1' :
                      'text-sm uppercase tracking-wider text-gray-500'
                    }`}>
                      Professional Summary
                    </h2>
                    <p className="text-sm text-gray-700 leading-relaxed">{data.summary}</p>
                  </div>
                )}

                {/* Experience */}
                {data.experience.length > 0 && (
                  <div className="mb-6">
                    <h2 className={`font-bold mb-3 ${
                      template === 'modern' ? 'text-lg text-teal-600 uppercase tracking-wide' :
                      template === 'classic' ? 'text-lg border-b border-gray-400 pb-1' :
                      'text-sm uppercase tracking-wider text-gray-500'
                    }`}>
                      Work Experience
                    </h2>
                    {data.experience.map((exp) => (
                      <div key={exp.id} className="mb-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold text-gray-800">{exp.title}</h3>
                            <p className={`text-sm ${template === 'modern' ? 'text-teal-600' : 'text-gray-600'}`}>
                              {exp.company} {exp.location && `- ${exp.location}`}
                            </p>
                          </div>
                          <span className="text-sm text-gray-500">
                            {exp.startDate} - {exp.endDate}
                          </span>
                        </div>
                        {exp.description && (
                          <p className="text-sm text-gray-700 mt-2 leading-relaxed">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Education */}
                {data.education.length > 0 && (
                  <div className="mb-6">
                    <h2 className={`font-bold mb-3 ${
                      template === 'modern' ? 'text-lg text-teal-600 uppercase tracking-wide' :
                      template === 'classic' ? 'text-lg border-b border-gray-400 pb-1' :
                      'text-sm uppercase tracking-wider text-gray-500'
                    }`}>
                      Education
                    </h2>
                    {data.education.map((edu) => (
                      <div key={edu.id} className="mb-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold text-gray-800">{edu.degree}</h3>
                            <p className="text-sm text-gray-600">
                              {edu.school} {edu.location && `- ${edu.location}`}
                            </p>
                          </div>
                          <span className="text-sm text-gray-500">{edu.year}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Skills */}
                {data.skills && (
                  <div>
                    <h2 className={`font-bold mb-2 ${
                      template === 'modern' ? 'text-lg text-teal-600 uppercase tracking-wide' :
                      template === 'classic' ? 'text-lg border-b border-gray-400 pb-1' :
                      'text-sm uppercase tracking-wider text-gray-500'
                    }`}>
                      Skills
                    </h2>
                    <div className={`flex flex-wrap gap-2 ${template === 'modern' ? '' : ''}`}>
                      {data.skills.split(',').map((skill, index) => (
                        <span
                          key={index}
                          className={`text-sm ${
                            template === 'modern'
                              ? 'bg-teal-50 text-teal-700 px-2 py-1 rounded'
                              : template === 'classic'
                              ? 'text-gray-700'
                              : 'text-gray-700'
                          }`}
                        >
                          {skill.trim()}{template !== 'modern' && index < data.skills.split(',').length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
